<template>
    <button
        :disabled="disabled"
        :class="[...baseClasses, {
            'is--disabled': props.disabled,
            'is--block': props.block,
            'is--loading': props.loading,
            'is--rounded': props.rounded,

        }, ]"
        :aria-disabled="disabled"
        :type="nativeType"
        :data-test="dataTestName"
        :data-testid="dataTestName || 'button-base'"
        @click="handleClick"
    >
        <div class="button__content">
            <i
                v-if="icon?.startsWith('vc-')"
                :class="icon"
                :style="{ fontSize: `${iconSizes[size] || 16}px` }"
                class="button__icon"
            />
            <redesign-base-icon
                v-else-if="icon"
                class="button__icon"
                :name="icon"
                :fill="'transparent'"
                :size="iconSizes[size]"
            />
            <span>
                <slot>
                    {{ label }}
                </slot>
            </span>
        </div>
        <RedesignBaseSpinner
            v-if="loading"
            :loader-icon="loaderIcon"
            :color="color"
            class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
        >
            <template #loader>
                <slot name="loader" />
            </template>
        </RedesignBaseSpinner>
    </button>
</template>

<script setup lang="ts">
import {
    TButtonColor,
    TButtonElement,
    TButtonSize,
    TButtonType
} from '@/components/RedesignBaseComponents/Buttons/buttons'
import { computed } from 'vue'
import RedesignBaseIcon from '@/components/RedesignBaseComponents/RedesignBaseIcon.vue'
import RedesignBaseSpinner from '@/components/RedesignBaseComponents/RedesignBaseSpinner.vue'

interface IButtonProps {
    disabled?: boolean
    loading?: boolean
    rounded?: boolean
    block?: boolean
    dataTestName?: string
    loaderIcon?: string
    size?: TButtonSize
    type?: TButtonType
    icon?: string
    label?: string
    color?: TButtonColor
    nativeType?: TButtonElement
}

const iconSizes = {
    small: 20,
    middle: 20,
    large: 24
}

/* Props */
const props = withDefaults(
    defineProps<IButtonProps>(),
    {
        dataTestName: 'base-button',
        loaderIcon: 'vc-icon-load',
        size: 'middle',
        type: 'default',
        icon: '',
        label: '',
        color: 'primary',
        nativeType: 'button'
    }
)
/* Emits */
const emit = defineEmits<{
    (e: 'click', pl: MouseEvent): void
}>()

/* Computed */
const baseClasses = computed(() => {
    return [
        'base-button',
        `base-button--${props.color}`,
        `base-button--${props.type}`,
        `base-button--${props.size}`
    ]
})

/* Methods */
function handleClick (evt: MouseEvent): void {
    if (!props.loading && !props.disabled) {
        emit('click', evt)
    }
}
</script>

<style lang="scss">
/*TODO: need to update next colors: text-gray-700 and --secondary-actions and --destructive-actions*/
$vc-button-transition: all .28s ease;
$hover-mix: 80%;
$active-mix: 65%;
button.base-button {
    --ring-outline-color: color-mix(in srgb, var(--ring-color) 50%, transparent);
    @apply
    relative
    inline-flex flex-shrink-0
    items-center
    cursor-pointer
    bg-transparent
    text-center
    outline-none
    rounded-sm
    overflow-hidden;
    transition: $vc-button-transition;
    height: max-content;

    .button__content {
        @apply flex text-center justify-center capitalize gap-2 items-center flex-grow flex-shrink-0 whitespace-nowrap leading-5 truncate;
        transition: $vc-button-transition;
    }

    &:not(.is--disabled):hover {
        background-color: var(--btn-hover-color);
    }

    &:not(.is--disabled):active {
        background-color: var(--btn-active-color);
    }

    &:not(.is--disabled):focus-visible {
        @apply ring ring-[var(--ring-outline-color)];
    }

    /* Sizes */
    &--middle {
        @apply text-sm rounded font-semibold px-4 py-2;
    }

    &--large {
        @apply text-base rounded font-semibold px-4-5 py-2.5;
        .button__content {
            @apply leading-6;
        }
    }

    &--small {
        @apply text-sm rounded font-medium;
        .button__content {
            @apply px-3.5 py-1.5;
        }
    }

    &--mini {
        @apply text-sm rounded font-medium;
        .button__content {
            @apply px-2 py-1.5 text-xs font-semibold;
        }
    }

    &.is--block {
        @apply w-full;
    }

    /* Colors */
    &--primary {
        --btn-hover-color: color-mix(in srgb, var(--primary-actions) #{$hover-mix}, #000);
        --btn-active-color: color-mix(in srgb, var(--primary-actions) #{$active-mix}, #000);
        --ring-color: var(--primary-actions);
        @apply bg-primary text-white;
    }

    &--secondary {
        --btn-hover-color: color-mix(in srgb, var(--secondary-actions) #{$hover-mix}, #000);
        --btn-active-color: color-mix(in srgb, var(--secondary-actions) #{$active-mix}, #000);
        --ring-color: var(--secondary-actions);
        @apply bg-secondary-actions text-white;
    }

    &--destructive {
        --btn-hover-color: color-mix(in srgb, var(--destructive-actions) #{$hover-mix}, #000);
        --btn-active-color: color-mix(in srgb, var(--destructive-actions) #{$active-mix}, #000);
        --ring-color: var(--destructive-actions);
        @apply bg-destructive-actions text-white;
    }


    &--outline {
        @apply bg-transparent;
        box-shadow: inset 0 0 0 0 #fff, inset 0 0 0 calc(1px + 0px) currentColor;

        &.base-button--primary {
            @apply text-primary;
            --btn-hover-color: color-mix(in srgb, var(--primary-actions) 20%, transparent);
            --btn-active-color: color-mix(in srgb, var(--primary-actions) 30%, transparent);
        }

        &.base-button--secondary {
            @apply text-gray-700;
            --btn-hover-color: color-mix(in srgb, var(--secondary-actions) 20%, transparent);
            --btn-active-color: color-mix(in srgb, var(--secondary-actions) 30%, transparent);
        }

        &.base-button--destructive {
            @apply text-destructive-actions;
            --btn-hover-color: color-mix(in srgb, var(--destructive-actions) 20%, transparent);
            --btn-active-color: color-mix(in srgb, var(--destructive-actions) 30%, transparent);
        }
    }

    &--soft {
        @apply text-current;
        &.base-button--primary {
            @apply text-primary-actions;
            --btn-hover-color: color-mix(in srgb, var(--primary-actions) 20%, transparent);
            --btn-active-color: color-mix(in srgb, var(--primary-actions) 30%, transparent);

            &:not(:hover):not(:active), &.is--disabled {
                background-color: color-mix(in srgb, var(--primary-actions) 10%, transparent);
            }
        }

        &.base-button--secondary {
            @apply text-gray-700;
            --btn-hover-color: color-mix(in srgb, var(--secondary-actions) 20%, transparent);
            --btn-active-color: color-mix(in srgb, var(--secondary-actions) 30%, transparent);

            &:not(:hover):not(:active), &.is--disabled {
                background-color: color-mix(in srgb, var(--secondary-actions) 10%, transparent);
            }
        }

        &.base-button--destructive {
            @apply text-destructive-actions;
            --btn-hover-color: color-mix(in srgb, var(--destructive-actions) 20%, transparent);
            --btn-active-color: color-mix(in srgb, var(--destructive-actions) 30%, transparent);

            &:not(:hover):not(:active), &.is--disabled {
                background-color: color-mix(in srgb, var(--destructive-actions) 10%, transparent);
            }
        }
    }

    &--text {
        @apply bg-transparent;
        &:not(.is--disabled):hover, &:not(.is--disabled):active {
            @apply bg-transparent;
        }

        &:not(.is--disabled):focus-visible {
            @apply ring-0;
        }

        &.base-button--primary {
            @apply text-primary-actions;
            --btn-hover-color: color-mix(in srgb, var(--primary-actions) 80%, #000);
            --btn-active-color: color-mix(in srgb, var(--primary-actions) 60%, #000);

            &:not(.is--disabled):hover {
                color: var(--btn-hover-color);
            }

            &:not(.is--disabled):active {
                color: var(--btn-active-color);
            }
        }

        &.base-button--secondary {
            @apply text-gray-700;
            --btn-hover-color: color-mix(in srgb, var(--gray-700) 80%, #000);
            --btn-active-color: color-mix(in srgb, var(--gray-700) 60%, #000);

            &:not(.is--disabled):hover {
                color: var(--btn-hover-color);
            }

            &:not(.is--disabled):active {
                color: var(--btn-active-color);
            }
        }

        &.base-button--destructive {
            @apply text-destructive-actions;
            --btn-hover-color: color-mix(in srgb, var(--destructive-actions) 80%, #000);
            --btn-active-color: color-mix(in srgb, var(--destructive-actions) 60%, #000);

            &:not(.is--disabled):hover {
                color: var(--btn-hover-color);
            }

            &:not(.is--disabled):active {
                color: var(--btn-active-color);
            }
        }
    }

    &.is--rounded {
        @apply rounded-2xl;
    }

    .button__loader {
        @apply absolute inset-0 w-full h-full flex items-center justify-center overflow-hidden;
        .loader-icon {
            @apply animate-spin transition-all duration-300;
        }
    }

    &.is--loading {
        @apply pointer-events-none;
        .button__content {
            @apply opacity-20;
        }
    }

    &.is--disabled {
        @apply select-none cursor-not-allowed opacity-50;
    }
}
</style>
